import React, { useState, useEffect, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { 
  Box, Typography, Button, Collapse, Grid, FormControl, InputLabel, Select, MenuItem, 
  TextField, Chip, Tooltip, Card, CardContent, CardActions, Accordion, AccordionSummary, 
  AccordionDetails, IconButton, Divider, alpha, Alert, Paper, CircularProgress
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon, FilterList as FilterIcon, Sort as SortIcon, 
  Refresh as RefreshIcon, DateRange as DateRangeIcon, KeyboardArrowUp as KeyboardArrowUpIcon,
  Visibility as VisibilityIcon, Gavel as GavelIcon, OpenInNew as OpenInNewIcon,
  Warning as WarningIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { paymentService } from '../../services/paymentService';
import { formatCurrency, formatDateTimeUniversal } from '../../utils/formatters';
import api from '../../services/api';
import { PAYMENT_STATUSES, PAYMENT_METHODS, RESERVATION_STATUSES, DEPOSIT_STATUSES, PAYMENT_TYPES } from '../../constants/paymentConstants';
import { useAuth } from '../../context/AuthContext';
import ReservationDetailsModal from './ReservationDetailsModal';
import DisputeSummaryCard from './DisputeSummaryCard';
import DateRangePicker from '../common/DateRangePicker';
import './PaymentHistoryInfinite.css';
import PenaltyExecutionSummary from '../disputes/PenaltyExecutionSummary';

// ─── Dispute helper functions (renter perspective) ───────────────────────────

/** Extract dispute id robustly */
const getDisputeId = (dispute) => {
  if (!dispute) return null;
  return dispute.id || dispute._id || dispute.dispute_id || dispute.disputeId || null;
};

/** Translated label for a dispute status */
const getDisputeStatusLabel = (status, t) => {
  if (t) {
    const key = `financial:dispute.statusShow.${status}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const labels = {
    pending: '⏳ En attente',
    in_review: '🔍 En révision',
    resolved: '✅ Résolu',
    cancelled: '❌ Annulé',
    needs_support: '🆘 Support nécessaire',
    escalated: '⚠️ Escaladé',
    closed: '🔒 Fermé',
    pending_settlement: '⏳ Règlement en attente',
  };
  return labels[status] || status || 'Inconnu';
};

/** MUI color for a dispute status */
const getDisputeStatusColor = (status) => {
  const colorMap = {
    pending: 'info',
    in_review: 'warning',
    resolved: 'success',
    cancelled: 'error',
    needs_support: 'warning',
    escalated: 'warning',
    closed: 'default',
    pending_settlement: 'info',
  };
  return colorMap[status] || 'default';
};

/** Translated label for a compensation type (renter perspective) */
const getCompensationTypeLabel = (type, t) => {
  if (t) {
    const key = `financial:dispute.compensationTypes.${type}`;
    const translated = t(key);
    if (translated && translated !== key) return translated;
  }
  const labels = {
    owner_compensation: t ? t('financial:compensation.capturedByOwner', '🔒 Dépôt capturé (dommages)') : '🔒 Dépôt capturé (dommages)',
    renter_refund: t ? t('financial:compensation.renterRefund', '💰 Remboursement obtenu') : '💰 Remboursement obtenu',
    reservation_cancellation: t ? t('financial:compensation.reservationCancelled', '❌ Annulation de réservation') : '❌ Annulation de réservation',
  };
  return labels[type] || type || 'Inconnu';
};

/**
 * Calculate renter's effective total cost after dispute resolution.
 *  baseCost = rental + commission + tax (NO deposit)
 *  - renter_refund      → baseCost - |compensation|
 *  - owner_compensation → baseCost + |compensation|  (deposit captured)
 *  - reservation_cancellation → 0
 */
const calculateRenterAdjustedCost = (baseCost, dispute) => {
  if (!dispute || !dispute.compensation_type) return parseFloat(baseCost) || 0;
  const total = parseFloat(baseCost) || 0;
  const compensation = Math.abs(parseFloat(dispute.compensation_amount) || 0);
  switch (dispute.compensation_type) {
    case 'renter_refund':            return total - compensation;
    case 'owner_compensation':       return total + compensation;
    case 'reservation_cancellation': return 0;
    default:                         return total;
  }
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Composant d'historique des paiements avec tri/filtres hybrides et pagination infinie
 * Architecture hybride optimisée : Frontend filtering + Backend chunking
 */
const PaymentHistoryInfinite = forwardRef(({ filters = {}, onError, disableAutoLoad = false }, ref) => {
  const { t } = useTranslation(['financial', 'common','disputes']);
  const { user: currentUser } = useAuth(); // Récupérer l'utilisateur actuel

  // Construit les filtres réellement supportés par le backend renter/payment-history
  // (type/status/method/start_date/end_date). Le reste (currency/reservationStatus) reste côté frontend.
  const buildBackendQueryFromLocalFilters = useCallback((lf) => {
    const query = {};

    if (lf?.paymentType) query.type = lf.paymentType;
    if (lf?.status) query.status = lf.status;

    // Backend ne supporte qu'un seul "method"; on envoie la valeur brute.
    if (lf?.paymentMethod) query.method = lf.paymentMethod;

    // Le backend n'applique la période que si start_date ET end_date sont présents.
    // Pour supporter un seul bound (dateFrom/dateTo), on complète avec un min/max.
    if (lf?.dateFrom || lf?.dateTo) {
      const start = lf?.dateFrom || '1970-01-01';
      const end = lf?.dateTo || new Date().toISOString().slice(0, 10);
      query.start_date = start;
      query.end_date = end;
    }

    return query;
  }, []);

  const serializeBackendQuery = useCallback((query) => {
    const entries = Object.entries(query || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
    entries.sort(([a], [b]) => a.localeCompare(b));
    return JSON.stringify(Object.fromEntries(entries));
  }, []);
  
  // États principaux - ARCHITECTURE HYBRIDE OPTIMISÉE
  const [allReservations, setAllReservations] = useState([]); // Cache en mémoire (limité)
  const [filteredReservations, setFilteredReservations] = useState([]); // Données filtrées côté frontend
  const [displayedReservations, setDisplayedReservations] = useState([]); // Données affichées avec pagination
  const [loading, setLoading] = useState(false);
  const loadingRef = useRef(false); // Ref pour éviter les problèmes de closure
  const sectionRef = useRef(null); // Ref pour la section des payments
  const scrollPositionRef = useRef(0); // Ref pour sauvegarder la position de scroll
  const filtersChangedRef = useRef(false); // Ref pour tracker si les filtres ont changé
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  
  // États pour le chargement backend par chunks
  // backendPage = prochaine page à charger (modèle OwnerPaymentHistoryInfinite)
  const [backendPage, setBackendPage] = useState(1);
  const [hasMoreBackend, setHasMoreBackend] = useState(true);

  // Garder en mémoire les filtres backend courants pour des appels cohérents (load more)
  const backendQueryRef = useRef({});
  const backendQueryKeyRef = useRef('');
  const hasInitializedRef = useRef(false);
  
  // États pour les options de filtres dynamiques
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availablePaymentTypes, setAvailablePaymentTypes] = useState([]);
  
  // État pour la modal de détails de réservation
  const [reservationModalOpen, setReservationModalOpen] = useState(false);
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  
  // États pour les filtres et le tri
  const [localFilters, setLocalFilters] = useState({
    status: '',
    paymentMethod: '',
    paymentType: '',
    currency: '',
    reservationStatus: '',
    dateFrom: '',
    dateTo: '',
    ...filters
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [sortBy, setSortBy] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // États pour les accordéons
  const [expandedItems, setExpandedItems] = useState(new Set());

  // États pour les litiges (lazy-load par réservation)
  const [disputesByReservation, setDisputesByReservation] = useState({});
  const [penaltiesByReservation, setPenaltiesByReservation] = useState({});
  const [penaltiesRetryByReservation, setPenaltiesRetryByReservation] = useState({});
  const [loadingDisputes, setLoadingDisputes] = useState({});

  // Configuration OPTIMISÉE pour performance
  const ITEMS_PER_PAGE = 5; // Affichage par page
  const BACKEND_CHUNK_SIZE = 10; // Taille raisonnable des chunks backend (réservations)
  const MAX_MEMORY_ITEMS = 200; // Limite de sécurité mémoire

  // 🚀 FILTRAGE OPTIMISÉ avec early returns
  const applyFiltersAndSort = useCallback((reservations, filters, sortBy, sortDirection) => {
    console.log('🔍 Applying OPTIMIZED filters to reservations:', { 
      totalReservations: reservations.length, 
      filters, 
      sortBy, 
      sortDirection 
    });
    
    // ⚡ Performance: Si pas de filtres et tri par défaut, retourner directement
    const hasFilters = filters.status || filters.paymentMethod || filters.paymentType || 
                      filters.currency || filters.reservationStatus || filters.dateFrom || filters.dateTo;
    if (!hasFilters && sortBy === 'date' && sortDirection === 'desc') {
      console.log('📈 No filters, returning original sorted list (FAST PATH)');
      return reservations;
    }
    
    // Étape 1: Filtrage avec optimisations
    let filtered = reservations;
    if (hasFilters) {
      filtered = reservations.filter(reservation => {
        // Early returns pour performance
        
        // Filtre par statut de réservation
        if (filters.reservationStatus && filters.reservationStatus !== '') {
          const reservationStatus = reservation.reservation_status || 'pending';
          if (reservationStatus !== filters.reservationStatus) return false;
        }
        
        // Filtre par devise
        if (filters.currency && filters.currency !== '') {
          const currency = reservation.currency || 'EUR';
          if (currency !== filters.currency) return false;
        }
        
        // Filtre par type de paiement (chercher dans tous les paiements)
        if (filters.paymentType && filters.paymentType !== '') {
          const hasPaymentType = Object.values(reservation.payment_details || {}).some(typeDetails => 
            typeDetails.payments && typeDetails.payments.some(p => p.payment_type === filters.paymentType)
          );
          if (!hasPaymentType) return false;
        }
        
        // Filtre par méthode de paiement (chercher dans tous les paiements)
        if (filters.paymentMethod && filters.paymentMethod !== '') {
          const hasPaymentMethod = Object.values(reservation.payment_details || {}).some(typeDetails => 
            typeDetails.payments && typeDetails.payments.some(p => {
              const method = p.payment_method || 'unknown';
              if (filters.paymentMethod === 'stripe' && !['stripe', 'card'].includes(method)) return false;
              if (filters.paymentMethod === 'cash' && method !== 'cash') return false;
              if (filters.paymentMethod === 'virement' && method !== 'virement') return false;
              return method === filters.paymentMethod;
            })
          );
          if (!hasPaymentMethod) return false;
        }
        
        // Filtre par statut de paiement (chercher dans tous les paiements)
        if (filters.status && filters.status !== '') {
          const hasPaymentStatus = Object.values(reservation.payment_details || {}).some(typeDetails => 
            typeDetails.payments && typeDetails.payments.some(p => p.status === filters.status)
          );
          if (!hasPaymentStatus) return false;
        }
        
        // Filtre par plage de dates
        if (filters.dateFrom || filters.dateTo) {
          const reservationDate = new Date(reservation.created_at || reservation.start_date);
          
          if (filters.dateFrom) {
            const fromDate = new Date(filters.dateFrom);
            fromDate.setHours(0, 0, 0, 0);
            if (reservationDate < fromDate) return false;
          }
          
          if (filters.dateTo) {
            const toDate = new Date(filters.dateTo);
            toDate.setHours(23, 59, 59, 999);
            if (reservationDate > toDate) return false;
          }
        }
        
        return true;
      });
    }
    
    // Étape 2: Tri optimisé
    if (sortBy) {
      filtered.sort((a, b) => {
        let valueA, valueB;
        
        switch (sortBy) {
          case 'date':
            valueA = new Date(a.created_at || a.start_date || 0);
            valueB = new Date(b.created_at || b.start_date || 0);
            break;
          case 'amount':
            valueA = parseFloat(a.total_amount || 0);
            valueB = parseFloat(b.total_amount || 0);
            break;
          case 'status':
            valueA = (a.reservation_status || 'pending').toLowerCase();
            valueB = (b.reservation_status || 'pending').toLowerCase();
            break;
          case 'paymentCount':
            valueA = a.payment_count || 0;
            valueB = b.payment_count || 0;
            break;
          default:
            return 0;
        }
        
        if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
        if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    console.log(`📊 OPTIMIZED filtering: ${reservations.length} → ${filtered.length} reservations`);
    return filtered;
  }, []);

  // 📄 Pagination côté frontend
  const applyPagination = useCallback((reservations, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return reservations.slice(0, endIndex); // Pagination cumulative
  }, []);

  // 🔍 Extraction des valeurs uniques pour les filtres dynamiques
  const updateAvailableFilterOptions = useCallback((reservations) => {
    console.log('🔍 Updating available filter options from reservations...');
    
    // Extraire les devises uniques
    const currencies = [...new Set(
      reservations
        .map(reservation => reservation.currency || 'EUR')
        .filter(Boolean)
    )].sort();
    
    // Extraire les méthodes de paiement uniques depuis tous les paiements
    const paymentMethods = [...new Set(
      reservations.flatMap(reservation => 
        Object.values(reservation.payment_details || {}).flatMap(typeDetails => 
          (typeDetails.payments || []).map(p => p.payment_method || 'unknown')
        )
      ).filter(method => method && method !== 'unknown')
    )].sort();
    
    // Extraire les statuts de paiement uniques
    const paymentStatuses = [...new Set(
      reservations.flatMap(reservation => 
        Object.values(reservation.payment_details || {}).flatMap(typeDetails => 
          (typeDetails.payments || []).map(p => p.status || 'pending')
        )
      ).filter(Boolean)
    )].sort();
    
    // Extraire les types de paiement uniques
    const paymentTypes = [...new Set(
      reservations.flatMap(reservation => 
        Object.values(reservation.payment_details || {}).flatMap(typeDetails => 
          (typeDetails.payments || []).map(p => p.payment_type || 'rental')
        )
      ).filter(Boolean)
    )].sort();
    
    console.log('💰 Available currencies:', currencies);
    console.log('💳 Available payment methods:', paymentMethods);
    console.log('📊 Available payment statuses:', paymentStatuses);
    console.log('🏷️ Available payment types:', paymentTypes);
    
    setAvailableCurrencies(currencies);
    setAvailablePaymentMethods(paymentMethods);
    setAvailableStatuses(paymentStatuses);
    setAvailablePaymentTypes(paymentTypes);
  }, []);

  // 🔄 Chargement par chunks depuis le backend
  const loadReservationsChunk = useCallback(async (chunkPage = 1, reset = false, explicitBackendQuery = null) => {
    console.log(`🔍 loadReservationsChunk called: page=${chunkPage}, loading=${loadingRef.current}`);
    if (loadingRef.current) {
      console.log('⚠️ Already loading, skipping chunk request');
      return { hasMore: false, newReservations: [], totalFromBackend: 0 };
    }
    
    console.log('🔧 Setting loading to TRUE');
    loadingRef.current = true;
    setLoading(true);
    if (reset) setError(null);
    
    try {
      console.log(`🔧 Loading CHUNK: page ${chunkPage}, size ${BACKEND_CHUNK_SIZE}`);

      const backendQueryToUse = explicitBackendQuery || backendQueryRef.current || {};
      
      const response = await paymentService.getRenterPaymentHistory({
        page: chunkPage,
        limit: BACKEND_CHUNK_SIZE,
        ...backendQueryToUse,
        component: 'PaymentHistoryOptimized'
      });
      
      if (!response?.success) throw new Error(response?.error || 'Failed to load payment history');
      
      const reservations = response.history || [];
      
      // Traitement optimisé des données
      const processedReservations = reservations.map((reservation, index) => {
        return {
          ...reservation,
          id: reservation.reservation_id || `reservation-${chunkPage}-${index}`,
          // Normaliser les champs pour cohérence
          reservation_status: reservation.reservation_status || 'pending',
          currency: reservation.currency || 'EUR',
          total_amount: reservation.total_amount || 0,
          payment_count: reservation.payment_count || 0,
          created_at: reservation.created_at || reservation.start_date,
          // S'assurer que payment_details existe
          payment_details: reservation.payment_details || {}
        };
      }).filter(reservation => reservation.id);
      
      const pagination = response.pagination || {};
      const hasMoreChunks = Boolean(
        pagination.has_next_page || 
        pagination.hasNext || 
        processedReservations.length === BACKEND_CHUNK_SIZE
      );
      
      console.log(`📊 Loaded CHUNK: ${processedReservations.length} reservations, hasMore: ${hasMoreChunks}`);
      
      return { 
        hasMore: hasMoreChunks, 
        newReservations: processedReservations,
        totalFromBackend: pagination.total_items || pagination.totalItems || 0
      };
      
    } catch (error) {
      console.error('❌ Error loading chunk:', error);
      setError(error.message || t('financial:errors.loadHistoryError', 'Erreur lors du chargement de l\'historique'));
      if (onError) onError(error);
      return { hasMore: false, newReservations: [], totalFromBackend: 0 };
    } finally {
      console.log('🔧 Setting loading to FALSE in finally block');
      loadingRef.current = false;
      setLoading(false);
      setInitialLoading(false);
    }
  }, [onError]);

  // 📥 Chargement intelligent de plus de données backend
  const loadMoreFromBackend = useCallback(async () => {
    if (!hasMoreBackend || loading) {
      console.log('⚠️ Cannot load more from backend:', { hasMoreBackend, loading });
      return;
    }
    
    // 🛡️ Protection mémoire
    if (allReservations.length >= MAX_MEMORY_ITEMS) {
      console.log(`⚠️ MEMORY LIMIT reached (${MAX_MEMORY_ITEMS}), stopping backend loading`);
      setHasMoreBackend(false);
      return;
    }
    
    const pageToLoad = backendPage;
    console.log(`📥 Loading MORE from backend: page ${pageToLoad}`);

    const result = await loadReservationsChunk(pageToLoad, false, backendQueryRef.current);
    
    if (result.newReservations.length > 0) {
      setAllReservations(prev => {
        const combined = [...prev, ...result.newReservations];
        // Éliminer doublons et appliquer limite mémoire
        const unique = combined.filter((r, i, arr) => arr.findIndex(x => x.id === r.id) === i);
        const limited = unique.slice(0, MAX_MEMORY_ITEMS);
        
        console.log(`🔧 Backend merge: ${prev.length} + ${result.newReservations.length} = ${limited.length} (limited)`);
        return limited;
      });
      setBackendPage(prev => prev + 1);
    }
    
    setHasMoreBackend(result.hasMore);
    
    if (result.totalFromBackend) {
      setTotalItems(result.totalFromBackend);
    }
  }, [hasMoreBackend, allReservations.length, backendPage, loadReservationsChunk, loading]);

  // 🚀 Chargement initial (ou reload backend). Option preserveUI=true pour éviter le clignotement.
  const loadInitialReservations = useCallback(async (explicitBackendQuery = null, options = {}) => {
    console.log('🚀 Loading INITIAL reservations chunk', { preserveUI: options?.preserveUI });

    const preserveUI = Boolean(options?.preserveUI);

    const backendQueryToUse = explicitBackendQuery || buildBackendQueryFromLocalFilters(localFilters);
    backendQueryRef.current = backendQueryToUse;
    backendQueryKeyRef.current = serializeBackendQuery(backendQueryToUse);
    
    // Reset des états - SAUF si preserveUI=true (pour éviter le flash "0 résultats")
    if (!preserveUI) {
      setAllReservations([]);
      setFilteredReservations([]);
      setDisplayedReservations([]);
      setInitialLoading(true);
    }
    
    setBackendPage(1);
    setHasMoreBackend(true);
    setCurrentPage(1);
    
    try {
      const result = await loadReservationsChunk(1, true, backendQueryToUse);
      
      if (result.newReservations.length > 0) {
        setAllReservations(result.newReservations);
        setHasMoreBackend(result.hasMore);

        // backendPage = prochaine page à charger
        setBackendPage(result.hasMore ? 2 : 1);
        
        if (result.totalFromBackend) {
          setTotalItems(result.totalFromBackend);
        }
      }
      
      console.log('✅ Initial reservations loaded successfully');
    } catch (error) {
      console.error('❌ Failed to load initial reservations:', error);
      setError(error.message || t('financial:errors.loadInitialError', 'Erreur lors du chargement initial'));
    } finally {
      // ✅ CORRECTION CRITIQUE: Toujours remettre initialLoading à false
      setInitialLoading(false);
      
      // 🔄 Restaurer la position de scroll après refresh
      if (scrollPositionRef.current > 0) {
        setTimeout(() => {
          window.scrollTo({
            top: scrollPositionRef.current,
            behavior: 'smooth'
          });
          console.log('🔄 Position scroll restaurée:', scrollPositionRef.current);
          scrollPositionRef.current = 0; // Reset après utilisation
        }, 100); // Petit délai pour laisser le DOM se mettre à jour
      }
    }
  }, [
    loadReservationsChunk,
    buildBackendQueryFromLocalFilters,
    serializeBackendQuery,
    localFilters,
  ]);

  // Reload backend quand les filtres BACKEND changent (type/status/method/date)
  useEffect(() => {
    if (!hasInitializedRef.current) return;
    if (disableAutoLoad) return;

    const nextBackendQuery = buildBackendQueryFromLocalFilters(localFilters);
    const nextKey = serializeBackendQuery(nextBackendQuery);

    if (nextKey === backendQueryKeyRef.current) return;

    console.log('🔄 Backend filters changed -> reloading from page 1', nextBackendQuery);
    // ⚠️ preserveUI: true = ne vide pas les listes pendant le fetch → pas de clignotement
    loadInitialReservations(nextBackendQuery, { preserveUI: true });
  }, [localFilters, disableAutoLoad, buildBackendQueryFromLocalFilters, serializeBackendQuery, loadInitialReservations]);

  // � Charge lazily le litige pour une réservation donnée
  const fetchDisputeForReservation = useCallback(async (reservationId) => {
    if (!reservationId) return;

    const fetchPenaltiesForDispute = async (dispute) => {
      const disputeId = getDisputeId(dispute);
      if (!disputeId) {
        setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: [] }));
        return;
      }

      try {
        const penaltyResponse = await api.get('/penalties', {
          params: {
            source_type: 'dispute',
            source_id: disputeId
          }
        });

        if (penaltyResponse.data?.success && penaltyResponse.data?.data) {
          const penaltiesRaw = Array.isArray(penaltyResponse.data.data) ? penaltyResponse.data.data : [penaltyResponse.data.data];
          const penalties = penaltiesRaw.filter(p => p?.status === 'applied' || p?.status === 'collected' || p?.status === 'pending_collection');
          setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: penalties }));
        } else {
          setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: [] }));
        }
      } catch (penaltyError) {
        console.error('Error fetching penalties:', penaltyError);
        setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: [] }));
      }
    };

    // If dispute cached, still fetch penalties if not fetched yet or retry once for resolved disputes
    const cachedDispute = disputesByReservation[reservationId];
    if (cachedDispute) {
      const cachedPenalties = penaltiesByReservation[reservationId];
      const shouldFetchInitially = cachedPenalties === undefined;
      const shouldRetryOnce = Array.isArray(cachedPenalties) && cachedPenalties.length === 0 && cachedDispute?.status === 'resolved' && !penaltiesRetryByReservation[reservationId];
      if (shouldRetryOnce) {
        setPenaltiesRetryByReservation(prev => ({ ...prev, [reservationId]: true }));
      }
      if (shouldFetchInitially || shouldRetryOnce) {
        await fetchPenaltiesForDispute(cachedDispute);
      }
      return;
    }

    // Déjà en cours ?
    if (loadingDisputes[reservationId]) return;
    setLoadingDisputes(prev => ({ ...prev, [reservationId]: true }));

    try {
      const response = await api.get(`/disputes?reservation_id=${reservationId}`);
      if (response.data?.success && response.data?.data) {
        const disputes = Array.isArray(response.data.data) ? response.data.data : [response.data.data];
        const dispute = disputes.length > 0 ? disputes[0] : null;
        setDisputesByReservation(prev => ({ ...prev, [reservationId]: dispute }));
        if (dispute) {
          await fetchPenaltiesForDispute(dispute);
        } else {
          setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: [] }));
        }
      } else {
        setDisputesByReservation(prev => ({ ...prev, [reservationId]: null }));
        setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: [] }));
      }
    } catch (err) {
      console.error('Error fetching dispute for reservation', reservationId, err);
      setDisputesByReservation(prev => ({ ...prev, [reservationId]: null }));
      setPenaltiesByReservation(prev => ({ ...prev, [reservationId]: [] }));
    } finally {
      setLoadingDisputes(prev => ({ ...prev, [reservationId]: false }));
    }
  }, [disputesByReservation, penaltiesByReservation, penaltiesRetryByReservation, loadingDisputes]);

  // �📄 Fonction pour afficher plus de résultats (pagination visuelle) - CORRECTED
  const loadMoreDisplayed = useCallback(() => {
    console.log(`🔄 Load more DISPLAYED triggered. Current page: ${currentPage}`);
    
    const maxPage = Math.ceil(filteredReservations.length / ITEMS_PER_PAGE);
    
    if (currentPage >= maxPage) {
      console.log('ℹ️ No more filtered reservations to display');
      return;
    }
    
    const nextPage = currentPage + 1;
    console.log(`📥 Showing more DISPLAYED reservations (page ${nextPage})`);
    
    // ✅ CORRECTION: On incrémente la page SANS déclencher de préchargement simultané
    setCurrentPage(nextPage);
    
    // 🔮 Préchargement séparé SEULEMENT si vraiment nécessaire ET avec délai
    const isReallyNearEnd = nextPage >= maxPage - 1;
    if (isReallyNearEnd && hasMoreBackend && filteredReservations.length < MAX_MEMORY_ITEMS && !loading) {
      console.log('🔮 SCHEDULING backend preload after UI update...');
      // Délai pour permettre à l'UI de se mettre à jour avant le préchargement
      setTimeout(() => {
        if (!loading && hasMoreBackend) {
          console.log('🔮 EXECUTING scheduled backend preload...');
          loadMoreFromBackend();
        }
      }, 300); // Délai suffisant pour éviter les conflits
    }
  }, [currentPage, filteredReservations.length, hasMoreBackend, loadMoreFromBackend]);

  // 🔄 Effet pour appliquer les FILTRES seulement - CORRECTED
  useEffect(() => {
    console.log('🔄 Applying FILTERS to cached reservations...');
    
    // Mettre à jour les options de filtres disponibles basées sur les vraies données
    updateAvailableFilterOptions(allReservations);
    
    // Appliquer seulement les filtres, pas le tri
    const filtered = allReservations.filter(reservation => {
      // Filtre par statut de réservation
      if (localFilters.reservationStatus && localFilters.reservationStatus !== '') {
        const reservationStatus = reservation.reservation_status || 'pending';
        if (reservationStatus !== localFilters.reservationStatus) return false;
      }
      
      // Filtre par devise
      if (localFilters.currency && localFilters.currency !== '') {
        const currency = reservation.currency || 'EUR';
        if (currency !== localFilters.currency) return false;
      }
      
      // Filtre par type de paiement
      if (localFilters.paymentType && localFilters.paymentType !== '') {
        const hasPaymentType = Object.values(reservation.payment_details || {}).some(typeDetails => 
          typeDetails.payments && typeDetails.payments.some(p => p.payment_type === localFilters.paymentType)
        );
        if (!hasPaymentType) return false;
      }
      
      // Filtre par méthode de paiement
      if (localFilters.paymentMethod && localFilters.paymentMethod !== '') {
        const hasPaymentMethod = Object.values(reservation.payment_details || {}).some(typeDetails => 
          typeDetails.payments && typeDetails.payments.some(p => {
            const method = p.payment_method || 'unknown';
            return method === localFilters.paymentMethod;
          })
        );
        if (!hasPaymentMethod) return false;
      }
      
      // Filtre par statut de paiement
      if (localFilters.status && localFilters.status !== '') {
        const hasPaymentStatus = Object.values(reservation.payment_details || {}).some(typeDetails => 
          typeDetails.payments && typeDetails.payments.some(p => p.status === localFilters.status)
        );
        if (!hasPaymentStatus) return false;
      }
      
      // Filtre par plage de dates
      if (localFilters.dateFrom || localFilters.dateTo) {
        const reservationDate = new Date(reservation.created_at || reservation.start_date);
        
        if (localFilters.dateFrom) {
          const fromDate = new Date(localFilters.dateFrom);
          fromDate.setHours(0, 0, 0, 0);
          if (reservationDate < fromDate) return false;
        }
        
        if (localFilters.dateTo) {
          const toDate = new Date(localFilters.dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (reservationDate > toDate) return false;
        }
      }
      
      return true;
    });
    
    setFilteredReservations(filtered);
    
    // ✅ Reset page seulement si c'est un changement de filtre, pas de données
    if (filtersChangedRef.current) {
      setCurrentPage(1);
      filtersChangedRef.current = false; // Reset le flag
      console.log('🔄 Page reset to 1 due to filter change');
    }
    
    console.log(`🔍 Filtering result: ${allReservations.length} → ${filtered.length} reservations`);
  }, [allReservations, localFilters, updateAvailableFilterOptions]); // ✅ Remise de localFilters pour que les filtres marchent

  // 🔄 Effet pour tracker les changements de filtres uniquement  
  useEffect(() => {
    console.log('🔄 Filters changed, marking for page reset');
    filtersChangedRef.current = true; // Marquer qu'un filtre a changé
  }, [localFilters]);

  // 📄 Effet pour appliquer la pagination ET le tri sur les données filtrées
  useEffect(() => {
    console.log('🔄 Applying pagination AND sort to filtered reservations...');
    // D'abord appliquer la pagination pour garder toutes les données affichées
    const paginated = filteredReservations.slice(0, currentPage * ITEMS_PER_PAGE);
    
    // Puis appliquer le tri sur les données paginées (déjà affichées)
    const sorted = [...paginated].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.created_at || a.start_date || 0);
          valueB = new Date(b.created_at || b.start_date || 0);
          break;
        case 'amount':
          valueA = parseFloat(a.total_amount || 0);
          valueB = parseFloat(b.total_amount || 0);
          break;
        case 'status':
          valueA = (a.reservation_status || 'pending').toLowerCase();
          valueB = (b.reservation_status || 'pending').toLowerCase();
          break;
        case 'paymentCount':
          valueA = a.payment_count || 0;
          valueB = b.payment_count || 0;
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    setDisplayedReservations(sorted);
    console.log(`📊 Sort applied: showing ${sorted.length} sorted reservations from ${paginated.length} paginated`);
  }, [filteredReservations, currentPage, sortBy, sortDirection]);

  // 🚀 Chargement initial au montage - SEULEMENT AU PREMIER MONTAGE
  useEffect(() => {
    if (hasInitializedRef.current) return;
    if (disableAutoLoad) {
      hasInitializedRef.current = true;
      return;
    }
    console.log('🚀 Initial component mount - loading first chunk');
    loadInitialReservations(buildBackendQueryFromLocalFilters(localFilters));
    hasInitializedRef.current = true;
  }, [disableAutoLoad, loadInitialReservations, buildBackendQueryFromLocalFilters, localFilters]);

  // 🎯 Exposition de méthodes pour le composant parent
  useImperativeHandle(ref, () => ({
    loadData: () => {
      console.log('🎯 Manual load triggered from parent');
      loadInitialReservations(buildBackendQueryFromLocalFilters(localFilters));
    },
    scrollToSection: () => {
      if (sectionRef.current) {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }), [loadInitialReservations, buildBackendQueryFromLocalFilters, localFilters]);

  // � Gestion du bouton "Scroll to Top"
  useEffect(() => {
    const handleScroll = () => {
      // Afficher le bouton après avoir scrollé 300px
      setShowScrollToTop(window.pageYOffset > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // �🔧 Gestionnaires d'événements
  const handleFilterChange = (field, value) => {
    console.log(`🔧 Filter changed: ${field} = ${value}`);
    setLocalFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // ⚡ Update dates en UNE SEULE FOIS (evite render cascade)
  const handleDateRangeChange = (start, end) => {
    setLocalFilters(prev => ({
      ...prev,
      dateFrom: start,
      dateTo: end
    }));
  };

  const handleSort = (field) => {
    console.log(`🔧 Sort changed: ${field}`);
    if (sortBy === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortDirection('desc');
    }
  };

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleRefresh = () => {
    console.log('🔄 Manual refresh triggered');
    
    // Sauvegarder la position actuelle de la section
    if (sectionRef.current) {
      const sectionRect = sectionRef.current.getBoundingClientRect();
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      scrollPositionRef.current = scrollTop + sectionRect.top;
      console.log('💾 Sauvegarde position scroll:', scrollPositionRef.current);
    }
    
    setError(null);
    loadInitialReservations(buildBackendQueryFromLocalFilters(localFilters));
  };

  // Fonction pour afficher la modal de détails de réservation
  const handleShowReservationDetails = (reservationId) => {
    console.log('🔍 Opening reservation details modal for ID:', reservationId);
    setSelectedReservationId(reservationId);
    setReservationModalOpen(true);
  };

  // Fonction pour fermer la modal de détails
  const handleCloseReservationDetails = () => {
    setReservationModalOpen(false);
    setSelectedReservationId(null);
  };

  // Fonction pour basculer l'expansion d'un accordéon
  const toggleExpanded = (reservationId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(reservationId)) {
        newSet.delete(reservationId);
      } else {
        newSet.add(reservationId);
        // Lazy-load le litige quand on ouvre l'accordéon
        fetchDisputeForReservation(reservationId);
      }
      return newSet;
    });
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  // Fonction pour formater la date avec l'heure (DD/MM/YYYY HH:MM)
  const formatDateWithTime = (dateString, t) => {
    if (!dateString) return t ? t('financial:dateUnknown', 'Date inconnue') : 'Date inconnue';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return t ? t('financial:dateInvalid', 'Date invalide') : 'Date invalide';
    }
  };
  
  // Fonction pour obtenir le libellé compréhensible du statut de paiement
  const getStatusLabel = (status) => {
    const statusKey = status?.toLowerCase?.() || status;
    
    // Mappage des valeurs de statuts vers les clés de traduction
    const statusKeyMap = {
      [PAYMENT_STATUSES.PENDING]: 'pending',
      [PAYMENT_STATUSES.COMPLETED]: 'completed',
      [PAYMENT_STATUSES.SUCCEEDED]: 'succeeded', 
      [PAYMENT_STATUSES.FAILED]: 'failed',
      [PAYMENT_STATUSES.PARTIAL]: 'pending',
      [PAYMENT_STATUSES.REFUNDED]: 'refunded',
      [PAYMENT_STATUSES.CANCELLED]: 'cancelled',
      [PAYMENT_STATUSES.NO_REFUND_NEEDED]: 'completed',
      'pending': 'pending',
      'completed': 'completed',
      'succeeded': 'succeeded',
      'failed': 'failed',
      'refunded': 'refunded',
      'cancelled': 'cancelled'
    };
    
    const translationKey = statusKeyMap[status] || statusKeyMap[statusKey] || 'pending';
    return t(`financial:status.${translationKey}`, status);
  };
  
  // Fonction pour obtenir le libellé compréhensible du moyen de paiement
  const getMethodLabel = (method) => {
    const methodKey = method?.toLowerCase?.() || method;
    
    // Mappage des valeurs de méthodes vers les clés de traduction
    const methodKeyMap = {
      [PAYMENT_METHODS.STRIPE]: 'stripe',
      [PAYMENT_METHODS.CASH]: 'cash', 
      [PAYMENT_METHODS.VIREMENT]: 'virement',
      [PAYMENT_METHODS.CARD]: 'card',
      [PAYMENT_METHODS.BANK_TRANSFER]: 'bank_transfer',
      [PAYMENT_METHODS.ADMIN_ADJUSTMENT]: 'other',
      [PAYMENT_METHODS.DEPOSIT_CAPTURE]: 'other',
      [PAYMENT_METHODS.STRIPE_REFUND]: 'stripe',
      [PAYMENT_METHODS.AUTOMATIC_CREDIT]: 'other',
      'stripe': 'stripe',
      'cash': 'cash',
      'virement': 'virement', 
      'card': 'card',
      'bank_transfer': 'bank_transfer'
    };
    
    const translationKey = methodKeyMap[method] || methodKeyMap[methodKey] || 'other';
    return t(`financial:methods.${translationKey}`, method);
  };
  
  // Fonction pour obtenir le libellé compréhensible du statut de réservation
  const getReservationStatusLabel = (status) => {
    const statusKey = status?.toLowerCase?.() || status;
    
    // Mappage des valeurs de statuts de réservation vers les clés de traduction
    const statusKeyMap = {
      [RESERVATION_STATUSES.PENDING]: 'pending',
      [RESERVATION_STATUSES.CONFIRMED]: 'confirmed',
      [RESERVATION_STATUSES.ACTIVE]: 'active',
      [RESERVATION_STATUSES.COMPLETED]: 'completed',
      [RESERVATION_STATUSES.CANCELLED]: 'cancelled',
      [RESERVATION_STATUSES.REFUNDED]: 'cancelled',
      [RESERVATION_STATUSES.EXPIRED]: 'expired',
      [RESERVATION_STATUSES.PAYMENT_FAILED]: 'cancelled',
      'pending': 'pending',
      'confirmed': 'confirmed',
      'active': 'active', 
      'completed': 'completed',
      'cancelled': 'cancelled',
      'expired': 'expired',
      'booked': 'confirmed',
      'draft': 'pending'
    };
    
    const translationKey = statusKeyMap[status] || statusKeyMap[statusKey] || 'unknown';
    return t(`financial:renter.reservationStatus.${translationKey}`, status);
  };
  
  // Fonction pour obtenir le libellé compréhensible du statut de dépôt
  const getDepositStatusLabel = (status) => {
    const statusKey = status?.toLowerCase?.() || status;
    
    // Mappage des valeurs de statuts de dépôt vers les clés de traduction
    const statusKeyMap = {
      [DEPOSIT_STATUSES.PENDING]: 'pending',
      [DEPOSIT_STATUSES.PAID]: 'completed',
      [DEPOSIT_STATUSES.REFUNDED]: 'refunded',
      [DEPOSIT_STATUSES.WITHHELD]: 'held',
      [DEPOSIT_STATUSES.HELD]: 'held',
      [DEPOSIT_STATUSES.PARTIALLY_CAPTURED]: 'pending',
      [DEPOSIT_STATUSES.RELEASED]: 'completed',
      [DEPOSIT_STATUSES.CANCELLED]: 'cancelled',
      'pending': 'pending',
      'completed': 'completed',
      'refunded': 'refunded',
      'held': 'held',
      'cancelled': 'cancelled'
    };
    
    const translationKey = statusKeyMap[status] || statusKeyMap[statusKey] || 'pending';
    return t(`financial:deposit.status.${translationKey}`, status);
  };

  // Fonction pour déterminer le type de paiement principal (cash vs online)
  const getPaymentTypeFlag = (reservation) => {
    const allPayments = Object.values(reservation.payment_details || {}).flatMap(typeDetails => 
      typeDetails.payments || []
    );
    
    if (allPayments.length === 0) {
      return { type: 'unknown', color: 'default', icon: '❓', label: t('financial:payment.flags.unknown', 'Inconnu') };
    }
    
    // Compter les paiements par type
    let cashCount = 0;
    let onlineCount = 0;
    
    allPayments.forEach(payment => {
      const method = payment.payment_method?.toLowerCase() || 'unknown';
      if (method === 'cash') {
        cashCount++;
      } else if (['stripe', 'card', 'virement', 'bank_transfer'].includes(method)) {
        onlineCount++;
      }
    });
    
    // Déterminer le type principal
    if (cashCount > onlineCount) {
      return { 
        type: 'cash', 
        color: 'warning', 
        icon: '💵',
        label: t('financial:payment.flags.cash', 'Espèces')
      };
    } else if (onlineCount > cashCount) {
      return { 
        type: 'online', 
        color: 'info', 
        icon: '💳',
        label: t('financial:payment.flags.online', 'En ligne')
      };
    } else if (cashCount > 0 && onlineCount > 0) {
      return { 
        type: 'mixed', 
        color: 'secondary', 
        icon: '🔄',
        label: t('financial:payment.flags.mixed', 'Mixte')
      };
    } else {
      return { 
        type: 'unknown', 
        color: 'default', 
        icon: '❓',
        label: t('financial:payment.flags.unknown', 'Inconnu')
      };
    }
  };

  // Rendu d'un détail de paiement par type
  const renderPaymentTypeDetails = (typeData, typeName, icon, currency = 'EUR') => {
    if (!typeData?.payments?.length) return null;

    return (
      <Box sx={{ mb: 2, p: 2, bgcolor: alpha('#000', 0.02), borderRadius: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1">{icon}</Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {typeName}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {formatCurrency(typeData.total, currency)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              ({t('financial:payment.paymentCount', '{{count}} paiement', { count: typeData.payments.length })})
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ ml: 4 }}>
          {typeData.payments.map((payment, index) => (
            <Box 
              key={payment.id || index} 
              sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                py: 0.5,
                borderBottom: index < typeData.payments.length - 1 ? 1 : 0,
                borderColor: 'divider'
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(payment.amount, payment.currency)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {getMethodLabel(payment.payment_method)}
                </Typography>
                <Chip 
                  label={getStatusLabel(payment.status)}
                  size="small"
                  color={
                    payment.status === 'completed' ? 'success' :
                    payment.status === 'pending' ? 'warning' :
                    payment.status === 'failed' ? 'error' : 'default'
                  }
                />
              </Box>
              <Typography variant="body2" color="text.secondary">
                {formatDate(payment.transaction_date)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error" sx={{ mb: 2 }}>
          ❌ Erreur lors du chargement de l'historique: {error}
        </Typography>
        <Button onClick={handleRefresh} variant="contained" color="primary">
          Réessayer
        </Button>
      </Box>
    );
  }

  return (
    <Box ref={sectionRef} sx={{ width: '100%' }}>
      {/* En-tête avec statistiques */}
      <Box 
        sx={{ 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: 'wrap'
        }}
      >
        <Typography variant="h6" sx={{ mb: { xs: 2, sm: 0 } }}>
          {initialLoading ? (
            <>{t('financial:payment.loading', 'Chargement de l\'historique...')}</>
          ) : displayedReservations.length > 0 ? (
            <>
              {displayedReservations.length} sur {filteredReservations.length} {t('financial:payment.title', 'Réservation(s)')}
              {filteredReservations.length !== totalItems && totalItems > 0 && ` (${totalItems} au total)`}
              {allReservations.length >= MAX_MEMORY_ITEMS && ` [Cache: ${allReservations.length}]`}
            </>
          ) : (
            <>{t('financial:payment.empty', 'Aucune réservation trouvée')}</>
          )}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterIcon />}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            size="small"
          >
            {isFilterOpen ? t('financial:payment.hideFilters', 'Masquer les filtres') : t('financial:payment.showFilters', 'Filtrer')}
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<SortIcon />}
            size="small"
            onClick={() => handleSort(sortBy === 'date' ? 'amount' : 'date')}
          >
            {sortBy === 'date' ? t('financial:payment.sortByAmount', 'Trier par montant') : t('financial:payment.sortByDate', 'Trier par date')}
          </Button>
        </Box>
      </Box>

      {/* Filtres avancés */}
      <Collapse in={isFilterOpen}>
        <Card sx={{ p: 2, mb: 3, bgcolor: alpha('#000', 0.02) }}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            🔍 Filtres avancés
          </Typography>
          
          <Grid container spacing={2}>
            {/* Statut de réservation */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="reservation-status-filter-label">{t('financial:payment.filters.reservationStatus', 'Statut')}</InputLabel>
                <Select
                  labelId="reservation-status-filter-label"
                  value={localFilters.reservationStatus}
                  onChange={(e) => handleFilterChange('reservationStatus', e.target.value)}
                  label={t('financial:payment.filters.reservationStatus', 'Statut')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allStatuses', 'Tous les statuts')}</MenuItem>
                  <MenuItem value="pending">{t('financial:renter.reservationStatus.pending', '⌛ En attente')}</MenuItem>
                  <MenuItem value="confirmed">{t('financial:renter.reservationStatus.confirmed', '✓ Confirmée')}</MenuItem>
                  <MenuItem value="active">{t('financial:renter.reservationStatus.active', '🔵 En cours')}</MenuItem>
                  <MenuItem value="completed">{t('financial:renter.reservationStatus.completed', '✅ Terminée')}</MenuItem>
                  <MenuItem value="cancelled">{t('financial:renter.reservationStatus.cancelled', '❌ Annulée')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Statut de paiement */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-status-filter-label">{t('financial:payment.filters.paymentStatus', 'Statut paiement')}</InputLabel>
                <Select
                  labelId="payment-status-filter-label"
                  value={localFilters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label={t('financial:payment.filters.paymentStatus', 'Statut paiement')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allStatuses', 'Tous les statuts')}</MenuItem>
                  {availableStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {getStatusLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Méthode de paiement */}
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="payment-method-filter-label">{t('financial:payment.filters.method', 'Méthode')}</InputLabel>
                <Select
                  labelId="payment-method-filter-label"
                  value={localFilters.paymentMethod}
                  onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  label={t('financial:payment.filters.method', 'Méthode')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allMethods', 'Toutes')}</MenuItem>
                  {availablePaymentMethods.map(method => (
                    <MenuItem key={method} value={method}>
                      {getMethodLabel(method)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Devise */}
            <Grid item xs={6} sm={3} md={1.4}>
              <FormControl fullWidth size="small">
                <InputLabel id="currency-filter-label">{t('financial:payment.filters.currency', 'Devise')}</InputLabel>
                <Select
                  labelId="currency-filter-label"
                  value={localFilters.currency}
                  onChange={(e) => handleFilterChange('currency', e.target.value)}
                  label={t('financial:payment.filters.currency', 'Devise')}
                >
                  <MenuItem value="">{t('financial:payment.filters.allCurrencies', 'Toutes')}</MenuItem>
                  {availableCurrencies.map(currency => (
                    <MenuItem key={currency} value={currency}>
                      {currency}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Plage de dates - DateRangePicker */}
            <Grid item xs={12} sm={4} md={2.8}>
              <DateRangePicker
                startDate={localFilters.dateFrom}
                endDate={localFilters.dateTo}
                onDateRangeChange={handleDateRangeChange}
                label={t('financial:payment.filters.dateRange', 'Période')}
                placeholder="jj/mm/aaaa → jj/mm/aaaa"
                size="small"
              />
            </Grid>
          </Grid>
          
          {/* Boutons d'actions pour les filtres */}
          <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              onClick={() => {
                setLocalFilters({
                  status: '',
                  paymentMethod: '',
                  paymentType: '',
                  currency: '',
                  reservationStatus: '',
                  dateFrom: '',
                  dateTo: ''
                });
              }}
            >
              {t('financial:payment.filters.clearFilters', 'Effacer les filtres')}
            </Button>
            
            {/* Raccourcis de plages de dates */}
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                handleDateRangeChange(weekAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]);
              }}
            >
              {t('financial:payment.filters.last7Days', '7 derniers jours')}
            </Button>
            
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                handleDateRangeChange(monthAgo.toISOString().split('T')[0], today.toISOString().split('T')[0]);
              }}
            >
              {t('financial:payment.filters.last30Days', '30 derniers jours')}
            </Button>
            
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                const today = new Date();
                const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
                handleDateRangeChange(firstDay.toISOString().split('T')[0], today.toISOString().split('T')[0]);
              }}
            >
              {t('financial:payment.filters.thisMonth', 'Ce mois')}
            </Button>
          </Box>
        </Card>
      </Collapse>

      {/* Liste des réservations */}
      <Box sx={{ mb: 3 }}>
        {initialLoading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography>{t('financial:payment.loadingHistory', '🔄 Chargement de l\'historique des paiements...')}</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error" sx={{ mb: 2 }}>
              ❌ Erreur: {error}
            </Typography>
            <Button onClick={handleRefresh} variant="contained" color="primary">
              {t('financial:payment.retry', 'Réessayer')}
            </Button>
          </Box>
        ) : displayedReservations.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="text.secondary">
              {localFilters.status || localFilters.paymentMethod || localFilters.paymentType || 
               localFilters.currency || localFilters.reservationStatus || localFilters.dateFrom || localFilters.dateTo
                ? t('financial:payment.noFiltersMatch', 'Aucune réservation ne correspond aux filtres sélectionnés')
                : t('financial:payment.empty', 'Aucune réservation trouvée')
              }
            </Typography>
          </Box>
        ) : (
          displayedReservations.map((reservation) => {
            const isExpanded = expandedItems.has(reservation.reservation_id || reservation.id);
            
            return (
              <Card 
                key={reservation.reservation_id || reservation.id} 
                sx={{ 
                  mb: 2, 
                  border: 1, 
                  borderColor: 'divider',
                  '&:hover': { borderColor: 'primary.main', boxShadow: 2 }
                }}
              >
                {/* En-tête de l'accordéon */}
                <CardContent 
                  sx={{ 
                    cursor: 'pointer', 
                    '&:hover': { bgcolor: alpha('#000', 0.02) } 
                  }}
                  onClick={() => toggleExpanded(reservation.reservation_id || reservation.id)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {reservation.reservation_title || t('financial:payment.reservation', 'Réservation')}
                        </Typography>
                        {/* Flag de type de paiement */}
                        {(() => {
                          const paymentFlag = getPaymentTypeFlag(reservation);
                          return (
                            <Chip
                              icon={<span style={{ fontSize: '0.8rem' }}>{paymentFlag.icon}</span>}
                              label={paymentFlag.label}
                              size="small"
                              color={paymentFlag.color}
                              variant="outlined"
                              sx={{ 
                                fontSize: '0.7rem',
                                height: '20px',
                                '& .MuiChip-label': { px: 1 }
                              }}
                            />
                          );
                        })()}
                      </Box>
                      {/* Date de création de la réservation (préférer created_at, fallback dépôt) */}
                      {(() => {
                        const createdAt = reservation.created_at || reservation.deposit?.created_at;
                        if (!createdAt) return null;
                        return (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            <CalendarIcon sx={{ fontSize: 12, mr: 0.5 }} />
                            {t('financial:dispute.createdAt', 'Créé le')} {formatDateWithTime(createdAt, t)}
                          </Typography>
                        );
                      })()}

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                        📍 {reservation.reservation_address} • {reservation.reservation_city}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        📅 {formatDate(reservation.start_date)} - {formatDate(reservation.end_date)}
                      </Typography>
                    </Box>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 2, minWidth: '180px' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 0.5 }}>
                        {(() => {
                          // Calculate header amount from breakdown (rental + commission + tax)
                          const pd = reservation.payment_details || {};
                          const rentalTotal = parseFloat(pd.rental?.total) || 0;
                          const commissionTotal = parseFloat(pd.commission?.total) || 0;
                          const taxTotal = parseFloat(pd.tax?.total) || 0;
                          let headerAmount = rentalTotal + commissionTotal + taxTotal;

                          // fallback to existing totals if breakdown missing
                          if (!headerAmount) {
                            headerAmount = parseFloat(reservation.total_amount_excluding_deposits || reservation.total_amount) || 0;
                          }
                          return formatCurrency(headerAmount, reservation.currency);
                        })()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontStyle: 'italic', mb: 1 }}>
                        {t('financial:payment.excludingDeposits', 'Dépôts non inclus')}
                      </Typography>
                      {reservation.reservation_status && (
                        <Chip 
                          label={getReservationStatusLabel(reservation.reservation_status)}
                          size="small"
                          color={
                            reservation.reservation_status === 'completed' ? 'success' :
                            reservation.reservation_status === 'confirmed' ? 'primary' :
                            reservation.reservation_status === 'cancelled' ? 'error' : 'default'
                          }
                        />
                      )}
                    </Box>

                    <IconButton>
                      <ExpandMoreIcon 
                        sx={{ 
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s'
                        }}
                      />
                    </IconButton>
                  </Box>
                </CardContent>

                {/* Contenu détaillé de l'accordéon */}
                <Collapse in={isExpanded}>
                  <Divider />
                  <CardContent>
                    {/* Résumé dispute embarqué dans la réservation (metadata) 
                    <DisputeSummaryCard reservation={reservation} />
                      */}
                    {/* ─── Détails des paiements par type ──────────────────────────────── */}
                    {renderPaymentTypeDetails(
                      reservation.payment_details?.rental || { payments: [], total: 0 }, 
                      t('financial:types.rental', 'Location'), 
                      '🏠',
                      reservation.currency
                    )}
                    {renderPaymentTypeDetails(
                      reservation.payment_details?.commission || { payments: [], total: 0 }, 
                      t('financial:types.commission', 'Commission'), 
                      '💼',
                      reservation.currency
                    )}
                    {renderPaymentTypeDetails(
                      reservation.payment_details?.tax || { payments: [], total: 0 }, 
                      t('financial:types.tax', 'Taxes'), 
                      '📊',
                      reservation.currency
                    )}

                    {/* ─── Section Pénalités ────────────────────────────────────────────── */}
                    {(() => {
                      const penaltyData = reservation.payment_details?.penalty;
                      const penaltyTotal = parseFloat(penaltyData?.total) || 0;
                      if (!penaltyTotal && !(penaltyData?.payments?.length > 0)) return null;
                      return (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <Divider sx={{ mb: 1.5 }} />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <WarningIcon sx={{ color: 'error.main', fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="error.dark">
                              {t('financial:types.penalty', 'Pénalités')}
                            </Typography>
                          </Box>
                          {/* Détail des paiements de pénalite */}
                          {renderPaymentTypeDetails(
                            penaltyData,
                            t('financial:types.penalty', 'Pénalités'),
                            '⚠️',
                            reservation.currency
                          )}
                          {/* Impact total des pénalités */}
                          {penaltyTotal > 0 && (
                            <Box sx={{ p: 1.5, bgcolor: alpha('#f44336', 0.06), borderRadius: 1, border: '1px solid', borderColor: alpha('#f44336', 0.2), mt: 1 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                {t('financial:penalty.impactLabel', 'Impact financier des pénalités')}:
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">
                                  {t('financial:penalty.baseCost', 'Coût de base')}:&nbsp;
                                  <strong>{formatCurrency(
                                    (reservation.total_amount_excluding_deposits || reservation.total_amount) - penaltyTotal,
                                    reservation.currency
                                  )}</strong>
                                </Typography>
                                <Typography variant="body2" color="error.main" fontWeight={600}>
                                  + {formatCurrency(penaltyTotal, reservation.currency)} {t('financial:penalty.inPenalties', 'en pénalités')}
                                </Typography>
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
                                {t('financial:penalty.penaltyOnOwnerPayout', 'Les pénalités sont prélevées sur le payout du propriétaire')}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}

                    {/* ─── Dépôt de garantie ───────────────────────────────────────────── */}
                    {reservation.deposit && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: alpha('#000', 0.02), borderRadius: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            🔒 {t('financial:types.deposit', 'Dépôt de Garantie')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {formatCurrency(reservation.deposit.amount, reservation.currency)}
                          </Typography>
                          <Chip 
                            label={getDepositStatusLabel(reservation.deposit.status)}
                            size="small"
                            color={
                              reservation.deposit.status === 'refunded' ? 'success' :
                              reservation.deposit.status === 'paid' ? 'primary' :
                              reservation.deposit.status === 'withheld' ? 'error' : 'default'
                            }
                          />
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(reservation.deposit.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {/* ─── Total avant litige (sans dépôt) ────────────────────────────── */}
                    {(() => {
                      const pd = reservation.payment_details || {};
                      const baseCost =
                        (parseFloat(pd.rental?.total)     || 0) +
                        (parseFloat(pd.commission?.total)  || 0) +
                        (parseFloat(pd.tax?.total)         || 0);
                      if (baseCost <= 0) return null;
                      return (
                        <Box sx={{ mt: 2, p: 1.5, bgcolor: alpha('#1976d2', 0.05), borderRadius: 1, border: '1px solid', borderColor: alpha('#1976d2', 0.2) }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                              {t('financial:summary.totalBeforeDispute', 'Total avant litige')} {t('financial:summary.breakdownLabel', '(Location + Commission + Taxes)')}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="primary.main">
                              {formatCurrency(baseCost, reservation.currency)}
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })()}

                    {/* ─── Section Litige (lazy-load depuis l'API) ────────────────────────────── */}

                    {/* Chargement du litige */}
                    {loadingDisputes[reservation.reservation_id] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5, p: 1.5, bgcolor: alpha('#1976d2', 0.05), borderRadius: 1 }}>
                        <CircularProgress size={18} />
                        <Typography variant="caption" color="text.secondary">
                          {t('financial:dispute.loadingDispute', 'Vérification d\'un litige en cours...')}
                        </Typography>
                      </Box>
                    )}

                    {/* Litige trouvé */}
                    {disputesByReservation[reservation.reservation_id] && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Divider sx={{ mb: 1.5 }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <GavelIcon sx={{ color: 'warning.main', fontSize: 20 }} />
                            <Typography variant="subtitle2" fontWeight={700} color="warning.dark">
                              {t('financial:dispute.title', 'Litige')}
                            </Typography>
                          </Box>
                          {getDisputeId(disputesByReservation[reservation.reservation_id]) && (
                            <Button
                              size="small"
                              variant="text"
                              endIcon={<OpenInNewIcon fontSize="small" />}
                              href={`/disputes/${getDisputeId(disputesByReservation[reservation.reservation_id])}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t('common:viewDetails', 'Voir')}
                            </Button>
                          )}
                        </Box>

                        <Grid container spacing={1.5}>
                          {/* Statut */}
                          <Grid item xs={12} sm={6}>
                            
                            <Box sx={{ mt: 0.5 }}>
                              <Chip
                                label={getDisputeStatusLabel(disputesByReservation[reservation.reservation_id].status, t)}
                                size="small"
                                color={getDisputeStatusColor(disputesByReservation[reservation.reservation_id].status)}
                              />
                            </Box>
                          </Grid>

                          {/* Dates - créé le */}
                          {disputesByReservation[reservation.reservation_id].created_at && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="caption" color="text.secondary">
                                {t('financial:dispute.createdAt', 'Ouvert le')}:
                              </Typography>
                              <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                {formatDateTimeUniversal(disputesByReservation[reservation.reservation_id].created_at)}
                              </Typography>
                            </Grid>
                          )}

                          {/* Détails si résolu */}
                          {disputesByReservation[reservation.reservation_id].status === 'resolved' && (
                            <>
                              {/* Type de compensation */}
                              {disputesByReservation[reservation.reservation_id].compensation_type && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('financial:dispute.compensationType', 'Issue')}:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                    {getCompensationTypeLabel(disputesByReservation[reservation.reservation_id].compensation_type, t)}
                                  </Typography>
                                </Grid>
                              )}

                              {/* Montant de compensation */}
                              {Math.abs(parseFloat(disputesByReservation[reservation.reservation_id].compensation_amount) || 0) > 0 && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('financial:dispute.compensationAmount', 'Montant concerné')}:
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                    sx={{
                                      mt: 0.5,
                                      color:
                                        disputesByReservation[reservation.reservation_id].compensation_type === 'renter_refund'
                                          ? 'success.main'
                                          : 'error.main',
                                    }}
                                  >
                                    {disputesByReservation[reservation.reservation_id].compensation_type === 'renter_refund' ? '-' : '+'}
                                    {formatCurrency(
                                      Math.abs(parseFloat(disputesByReservation[reservation.reservation_id].compensation_amount) || 0),
                                      reservation.currency
                                    )}
                                  </Typography>
                                </Grid>
                              )}

                              {/* Date de résolution */}
                              {disputesByReservation[reservation.reservation_id].resolved_at && (
                                <Grid item xs={12} sm={6}>
                                  <Typography variant="caption" color="text.secondary">
                                    {t('financial:dispute.resolvedAt', 'Résolu le')}:
                                  </Typography>
                                  <Typography variant="body2" fontWeight="medium" sx={{ mt: 0.5 }}>
                                    {formatDateTimeUniversal(disputesByReservation[reservation.reservation_id].resolved_at)}
                                  </Typography>
                                </Grid>
                              )}

                              {/* Coût effectif ajusté */}
                              {disputesByReservation[reservation.reservation_id].compensation_type && (
                                <Grid item xs={12}>
                                  <Box
                                    sx={{
                                      p: 1.5,
                                      mt: 0.5,
                                      borderRadius: 1,
                                      bgcolor:
                                        disputesByReservation[reservation.reservation_id].compensation_type === 'renter_refund'
                                          ? alpha('#4caf50', 0.08)
                                          : disputesByReservation[reservation.reservation_id].compensation_type === 'reservation_cancellation'
                                          ? alpha('#9e9e9e', 0.08)
                                          : alpha('#f44336', 0.08),
                                      border: '1px solid',
                                      borderColor:
                                        disputesByReservation[reservation.reservation_id].compensation_type === 'renter_refund'
                                          ? alpha('#4caf50', 0.3)
                                          : disputesByReservation[reservation.reservation_id].compensation_type === 'reservation_cancellation'
                                          ? alpha('#9e9e9e', 0.3)
                                          : alpha('#f44336', 0.3),
                                    }}
                                  >
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                      {t('financial:summary.renterAdjustedCost', 'Coût effectif après litige')}:
                                    </Typography>
                                    <Typography
                                      variant="h6"
                                      fontWeight="bold"
                                      sx={{
                                        color:
                                          disputesByReservation[reservation.reservation_id].compensation_type === 'renter_refund'
                                            ? 'success.main'
                                            : 'error.main',
                                      }}
                                    >
                                      {(() => {
                                        const dispute = disputesByReservation[reservation.reservation_id];
                                        // Base = rental + commission + tax exactement comme affiché, SANS le dépôt
                                        const pd = reservation.payment_details || {};
                                        const baseCost =
                                          (parseFloat(pd.rental?.total)     || 0) +
                                          (parseFloat(pd.commission?.total)  || 0) +
                                          (parseFloat(pd.tax?.total)         || 0);
                                        return formatCurrency(
                                          calculateRenterAdjustedCost(baseCost, dispute),
                                          reservation.currency
                                        );
                                      })()}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {disputesByReservation[reservation.reservation_id].compensation_type === 'owner_compensation'
                                        ? t('financial:compensation.capturedByOwner', '🔒 Dépôt capturé (dommages)')
                                        : disputesByReservation[reservation.reservation_id].compensation_type === 'renter_refund'
                                        ? t('financial:compensation.deductedFromCost', 'Remboursement déduit du coût (hors dépôt)')
                                        : t('financial:compensation.reservationCancelled', '❌ Annulation de réservation')
                                      }
                                    </Typography>

                                    {/* Détail capture caution (visible quand l'owner récupère une compensation) */}
                                    {(() => {
                                      const dispute = disputesByReservation[reservation.reservation_id];
                                      if (!dispute || dispute.compensation_type !== 'owner_compensation' || !reservation.deposit) return null;
                                      const depositAmt = parseFloat(reservation.deposit.amount) || 0;
                                      const comp = Math.abs(parseFloat(dispute.compensation_amount) || 0);
                                      const remaining = depositAmt - comp;

                                      return (
                                        <Box sx={{ mt: 1 }}>
                                          <Typography variant="body2">
                                            • {t('compensation.originalDeposit', 'Caution initiale')} : <strong>{formatCurrency(depositAmt, reservation.currency)}</strong>
                                          </Typography>
                                          <Typography variant="body2" color="error.dark">
                                            • {t('compensation.deductedAmount', 'Montant retenu (dommages)')} : <strong>-{formatCurrency(comp, reservation.currency)}</strong>
                                          </Typography>
                                          <Typography variant="body2" color="success.dark" sx={{ mt: 0.5 }}>
                                            • {t('compensation.refundedAmountLabel', 'Montant rendu au locataire')} : <strong>{remaining > 0 ? formatCurrency(remaining, reservation.currency) : t('financial:compensation.noneReturned', 'Aucun montant remboursé')}</strong>
                                          </Typography>
                                        </Box>
                                      );
                                    })()}
                                  </Box>
                                </Grid>
                              )}
                            </>
                          )}
                        </Grid>
                      </Box>
                    )}

                    {/* Penalty Execution Summary Section */}
                    {penaltiesByReservation[reservation.reservation_id] && penaltiesByReservation[reservation.reservation_id].length > 0 && currentUser && (
                      <Box sx={{ mt: 2, mb: 2 }}>
                        <Divider sx={{ mb: 1 }} />
                        {penaltiesByReservation[reservation.reservation_id]
                          .filter(p => p?.user_id === currentUser.id || p?.execution_metadata?.victim_compensation?.victim_user_id === currentUser.id)
                          .map(p => (
                            <Accordion key={p.id} sx={{ mb: 1 }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 220 }}>
                                        <Typography variant="subtitle2" fontWeight={600}>
                                          {t('financial:types.penalty', 'Pénalités')}
                                        </Typography>
                                        <Box sx={{ flex: 1, height: 1, bgcolor: 'divider', mx: 1 }} />
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                          {`#${String(p.id)}`}
                                        </Typography>
                                      </Box>
                                      <Chip label={`${formatCurrency(p.amount || p.execution_metadata?.total_collected || 0, reservation.currency)}`} size="small" color="error" />
                                      <Chip label={t(`disputes:penalties.execution.statuses.${p.status}`, p.status)} size="small" variant="outlined" />
                                    </Box>
                                  <Typography variant="caption" color="text.secondary">
                                    {p.applied_at ? formatDateTimeUniversal(p.applied_at) : (p.created_at ? formatDateTimeUniversal(p.created_at) : '')}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails>
                                <PenaltyExecutionSummary penalty={p} dispute={disputesByReservation[reservation.reservation_id]} />
                              </AccordionDetails>
                            </Accordion>
                        ))}
                      </Box>
                    )}

                    {/* Annulation sans litige → coût effectif = 0 */}
                    {!loadingDisputes[reservation.reservation_id] &&
                      !disputesByReservation[reservation.reservation_id] &&
                      reservation.reservation_status === 'cancelled' && (
                        <Box sx={{ mt: 2, mb: 1 }}>
                          <Divider sx={{ mb: 1.5 }} />
                          <Box sx={{ p: 1.5, bgcolor: alpha('#9e9e9e', 0.07), borderRadius: 1, border: '1px solid', borderColor: alpha('#9e9e9e', 0.2) }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                              {t('financial:dispute.renterAdjustedCost', 'Coût effectif')}:
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" color="text.secondary">
                              {formatCurrency(0, reservation.currency)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              ({t('financial:reservationStatus.cancelledNoBenefit', 'Réservation annulée – aucun montant dû')})
                            </Typography>
                          </Box>
                        </Box>
                    )}
                    
                    {/* Bouton pour voir les détails de la réservation */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                      <Button
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleShowReservationDetails(reservation.reservation_id)}
                        sx={{
                          borderColor: 'primary.main',
                          color: 'primary.main',
                          '&:hover': {
                            borderColor: 'primary.dark',
                            backgroundColor: alpha('#000', 0.04)
                          }
                        }}
                      >
                        {t('financial:payment.viewDetails', 'Voir détails de la réservation')}
                      </Button>
                    </Box>
                  </CardContent>
                </Collapse>
              </Card>
            );
          })
        )}
      </Box>

      {/* Section des boutons d'action */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2, gap: 2 }}>
        {/* Bouton "Load More" - Affiche plus de résultats filtrés */}
        {displayedReservations.length < filteredReservations.length && !loading && !initialLoading && (
          <Button
            variant="contained"
            color="primary"
            onClick={loadMoreDisplayed}
            startIcon={<ExpandMoreIcon />}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={loading}
          >
            {t('financial:payment.loadMoreReservations', 'Charger plus de réservations')} ({displayedReservations.length}/{filteredReservations.length})
          </Button>
        )}
        
        {/* Bouton pour charger plus de données backend */}
        {displayedReservations.length >= filteredReservations.length && hasMoreBackend && allReservations.length < MAX_MEMORY_ITEMS && !loading && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={loadMoreFromBackend}
            startIcon={<ExpandMoreIcon />}
            sx={{ borderRadius: 2, px: 3 }}
            disabled={loading}
          >
            {t('financial:payment.loadMoreFromServer', 'Charger plus depuis le serveur')} ({allReservations.length}/{totalItems})
          </Button>
        )}
        
        {/* Message si toutes les réservations filtrées sont affichées */}
        {displayedReservations.length > 0 && displayedReservations.length >= filteredReservations.length && !hasMoreBackend && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
            {t('financial:payment.allDisplayed', '✅ Toutes les réservations correspondantes sont affichées')} ({displayedReservations.length}/{filteredReservations.length})
          </Typography>
        )}
        
        {/* Bouton "Refresh" */}
        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          startIcon={<RefreshIcon />}
          sx={{ borderRadius: 2, px: 3 }}
          disabled={loading || initialLoading}
        >
          {t('common:refresh', 'Actualiser')}
        </Button>
      </Box>
      
      {/* Informations de statut OPTIMISÉES */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {displayedReservations.length > 0 ? (
            <>
              {filteredReservations.length !== totalItems 
                ? t('financial:payment.displayedCountWithTotal', '📊 {{displayed}} affiché(s) sur {{filtered}} correspondant(s) ({{total}} au total)', {
                    displayed: displayedReservations.length,
                    filtered: filteredReservations.length,
                    total: totalItems
                  })
                : t('financial:payment.displayedCount', '📊 {{displayed}} affiché(s) sur {{filtered}} correspondant(s)', {
                    displayed: displayedReservations.length,
                    filtered: filteredReservations.length
                  })
              }
              {allReservations.length >= MAX_MEMORY_ITEMS && 
                ` ${t('financial:payment.cacheLimited', '💾 Cache limité à {{limit}}', { limit: MAX_MEMORY_ITEMS })}`
              }
            </>
          ) : !loading && !initialLoading ? (
            <>
              {localFilters.status || localFilters.paymentMethod || localFilters.paymentType || 
               localFilters.currency || localFilters.reservationStatus || localFilters.dateFrom || localFilters.dateTo
                ? t('financial:payment.noMatchingReservations', '🔍 Aucune réservation ne correspond aux critères de filtrage')
                : t('financial:payment.noReservations', '📭 Aucune réservation dans l\'historique')
              }
            </>
          ) : null}
        </Typography>
      </Box>

      {/* Bouton Scroll to Top flottant */}
      {showScrollToTop && (
        <Box
          onClick={handleScrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: 'primary.main',
            color: 'white',
            borderRadius: '50%',
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: 3,
            zIndex: 1000,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'primary.dark',
              transform: 'scale(1.1)',
              boxShadow: 6,
            },
            '&:active': {
              transform: 'scale(0.95)',
            }
          }}
        >
          <KeyboardArrowUpIcon sx={{ fontSize: 28 }} />
        </Box>
      )}
      
      {/* Modal de détails de réservation */}
      <ReservationDetailsModal
        reservationId={selectedReservationId}
        open={reservationModalOpen}
        onClose={handleCloseReservationDetails}
        currentUser={currentUser} // Passer l'utilisateur actuel pour la messagerie
      />
    </Box>
  );
});

export default PaymentHistoryInfinite;
